import React, { useState, useEffect } from "react";
import { useFetchGrantData, useFetchWriterAnswers, updateGrantQuestions } from "../hooks/FetchFromFirebase";

export function ApplicationQuestionDisplay({ grantID, uid, clientID }) {
    const { data: fetchedQuestions, loading, error, setData: setFetchedQuestions } = useFetchGrantData(grantID);

    const [confirmedQuestions, setConfirmedQuestions] = useState(new Set());
    const [visibleCount, setVisibleCount] = useState(0);
    const [editingIndex, setEditingIndex] = useState(null);
    const [editValue, setEditValue] = useState("");

    // Calculate if all questions are confirmed so we can start polling for answers
    const allQuestionsConfirmed = fetchedQuestions?.length > 0 && confirmedQuestions.size === fetchedQuestions.length;

    const fetchedAnswers = useFetchWriterAnswers(
        uid,
        clientID,
        grantID,
        allQuestionsConfirmed
    );

    const handleEditClick = (index, currentText) => {
        setEditingIndex(index);
        setEditValue(currentText);
    };

    const handleDiscard = () => {
        setEditingIndex(null);
        setEditValue("");
    };

    const handleSaveEdit = async (index) => {
        if (!editValue.trim() || editValue === fetchedQuestions[index]) {
            handleDiscard();
            return;
        }

        const newQuestions = [...fetchedQuestions];
        newQuestions[index] = editValue;
        
        // Optimistically update the UI
        setFetchedQuestions(newQuestions);
        setEditingIndex(null);
        try {
            await updateGrantQuestions(grantID, newQuestions);
        } catch (err) {
            console.error("Failed to save edited question:", err);
            // Optionally: revert to old questions if it fails
            // setFetchedQuestions(fetchedQuestions);
        }
    };

    useEffect(() => {
        if (!grantID) return;
        const saved = localStorage.getItem(`grantAide_confirmed_${grantID}`);
        if (saved) {
            try {
                setConfirmedQuestions(new Set(JSON.parse(saved)));
            } catch (e) {
                console.error("Failed to parse confirmed questions", e);
            }
        }
    }, [grantID]);

    const toggleConfirm = (index) => {
        setConfirmedQuestions((prev) => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            
            if (grantID) {
                localStorage.setItem(`grantAide_confirmed_${grantID}`, JSON.stringify(Array.from(next)));
            }
            
            return next;
        });
    };

    // Sync state visually
    useEffect(() => {
        if (!grantID) return;
        const saved = localStorage.getItem(`grantAide_progress_${grantID}`);
        setVisibleCount(saved ? parseInt(saved, 10) : 0);
    }, [grantID]);

    useEffect(() => {
        if (!fetchedQuestions || fetchedQuestions.length === 0) return;
        const saved = localStorage.getItem(`grantAide_progress_${grantID}`);
        let currentCount = saved ? parseInt(saved, 10) : 0;
        if (currentCount === 0) {
            currentCount = Math.min(3, fetchedQuestions.length);
            setVisibleCount(currentCount);
            localStorage.setItem(`grantAide_progress_${grantID}`, currentCount);
        }
        if (currentCount >= fetchedQuestions.length) return;
        const timer = setInterval(() => {
            setVisibleCount((prev) => {
                const nextCount = Math.min(prev + 3, fetchedQuestions.length);
                localStorage.setItem(`grantAide_progress_${grantID}`, nextCount);
                if (nextCount >= fetchedQuestions.length) clearInterval(timer);
                return nextCount;
            });
        }, 15000);
        return () => clearInterval(timer);
    }, [fetchedQuestions, grantID]);

    const visibleQuestions = fetchedQuestions ? fetchedQuestions.slice(0, visibleCount) : [];

    if (loading || (!fetchedQuestions && !error)) {
        return (
            <div className="flex-1 flex flex-col h-full bg-[#0d0024] overflow-hidden">
                <div className="border border-dashed border-[#6C35FF] rounded-xl p-6 bg-[#12002e] text-purple-400 text-sm font-mono flex items-center justify-center animate-pulse w-full max-w-md">
                    Fetching Grant Data...
                                    </div>
                                    </div>
                    );
}

    return (
        <div className="flex-1 flex flex-col h-full bg-[#0d0024] overflow-hidden">
            <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                {visibleQuestions.map((q, index) => {
                    const isConfirmed = confirmedQuestions.has(index);
                    const isEditing = editingIndex === index;
                    // Writer answer keys are question numbers (1-based), while `index` is 0-based.
                    const answer = fetchedAnswers?.[index + 1];
                    const hasAnswer = Array.isArray(answer)
                        ? answer.length > 0
                        : answer && typeof answer === "object"
                            ? Object.values(answer).some((writerAnswer) => Boolean(writerAnswer))
                            : Boolean(answer);

                    return (
                        <React.Fragment key={index}>
                            <div className={`border border-[#6C35FF] rounded-xl p-3 bg-[#0d0024] flex gap-[9px] animate-in fade-in duration-700 ${isConfirmed ? 'border-opacity-50' : ''}`}>
                                <div className="text-gray-400 text-sm font-mono w-[30px] shrink-0 pt-1 select-none">Q{index + 1}</div>
                                <div className={`flex-1 flex flex-col rounded-lg overflow-hidden border ${isConfirmed ? 'border-transparent' : 'border-white'} transition-all duration-500`}>
                                    <div className={`${isConfirmed ? 'bg-transparent' : 'bg-[#a100ff]'} px-3 py-3 text-white text-sm leading-relaxed font-sans text-left transition-colors duration-500`}>
                                        {isEditing ? (
                                            <textarea
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                className="w-full bg-[#1a0033] text-white p-2 rounded border border-[#6C35FF] focus:outline-none focus:border-[#00F0FF] min-h-[100px] resize-y custom-scrollbar whitespace-pre-wrap"
                                            />
                                        ) : (
                                            <div className="whitespace-pre-line">{q}</div>
                                        )}
                                    </div>
                                    <div className={`${isConfirmed ? 'bg-transparent' : 'bg-[#09001a]'} px-3 h-[28px] flex items-center gap-4 border-t ${isConfirmed ? 'border-transparent' : 'border-white'} text-sm font-medium transition-colors duration-500`}>
                                        {isEditing ? (
                                            <>
                                                <button 
                                                    onClick={() => handleSaveEdit(index)}
                                                    className="transition-colors duration-300 text-[#009939] hover:text-green-300"
                                                >
                                                    Save
                                                </button>
                                                <button 
                                                    onClick={handleDiscard}
                                                    className="transition-colors duration-300 text-[#EF4444] hover:text-red-400"
                                                >
                                                    Discard
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button 
                                                    onClick={() => toggleConfirm(index)}
                                                    className={`transition-colors duration-300 ${isConfirmed ? 'text-[#C81ECE]' : 'text-[#22C55F] hover:text-green-400'}`}
                                                >
                                                    {isConfirmed ? 'Confirmed' : 'Confirm'}
                                                </button>
                                                {!isConfirmed && (
                                                    <button 
                                                        onClick={() => handleEditClick(index, q)}
                                                        className="transition-colors duration-300 text-gray-300 hover:text-white"
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* --- DYNAMIC ANSWER BLOCK --- */}
                            {isConfirmed && hasAnswer && (
                                <div className="bg-gradient-to-br from-[#00F0FF] via-[#5406D2] to-[#12002e] p-[1.5px] rounded-xl animate-in slide-in-from-bottom-4 duration-1000">
                                    <div className="bg-[#0d0024] p-3 rounded-xl flex flex-col">
                                        <div className="flex gap-[9px]">
                                            <div className="w-[30px] shrink-0 flex flex-col items-start pt-1 text-xs font-mono text-gray-400 select-none">
                                                <span>A{index + 1}</span>
                                            </div>
                                            <div className="flex-1 flex flex-col rounded-lg overflow-hidden border-2 border-[#9D4EDD]">
                                                <div className="bg-[#E5E7EB] text-black px-3 py-3 text-sm whitespace-pre-wrap leading-relaxed min-h-[140px] font-sans text-left flex flex-col gap-4">
                                                    {answer && typeof answer === "object" && !Array.isArray(answer) ? (
                                                        Object.entries(answer).map(([writerType, writerAnswer]) => (
                                                            <div key={writerType} className="border-b border-gray-300 pb-4 last:border-0 last:pb-0">
                                                                <div>{writerAnswer}</div>
                                                            </div>
                                                        ))
                                                    ) : Array.isArray(answer) ? (
                                                        answer.map((answerData, i) => (
                                                            <div key={`${answerData.agent || "answer"}-${i}`} className="border-b border-gray-300 pb-4 last:border-0 last:pb-0">
                                                                {answerData.agent && (
                                                                    <span className="font-bold text-gray-700 block mb-1">
                                                                        {answerData.agent} answered:
                                                                    </span>
                                                                )}
                                                                <div>{answerData.text ?? answerData}</div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div>{answer}</div>
                                                    )}
                                                </div>
                                                <div className="bg-[#09001a] px-3 h-[28px] flex items-center gap-4 border-t-2 border-[#9D4EDD] text-sm font-medium">
                                                    <button className="text-[#009939] hover:text-green-300 transition-colors">Save</button>
                                                    <button className="text-[#EF4444] hover:text-red-400 transition-colors">Undo</button>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="mt-3 ml-[39px]">
                                            <div className="text-sm flex flex-wrap gap-x-2 gap-y-1 items-center select-none">
                                                <span className="text-gray-400 font-semibold mr-1">Rubrics:</span>
                                                {["R1", "R2", "R5", "R8", "R9", "R13", "R14"].map((r) => (
                                                    <span key={r} className="text-[#a855f7] font-mono text-xs cursor-pointer hover:text-purple-300">{r}</span>
                                                ))}
                                                <span className="text-white font-bold font-mono text-xs bg-[#2a0050] px-1.5 py-0.5 rounded border border-[#5406D2]">R15</span>
                                            </div>
                                            <div className="text-xs text-gray-300 mt-3 flex items-start justify-between gap-4 bg-[#12002e] p-3 rounded-lg border border-[#2a0050]">
                                                <p className="leading-relaxed"><span className="font-bold text-white mr-1">R15</span> For the target market focus...</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}

